import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, ChevronDown } from 'lucide-react';
import { hasDecided, setConsent, rehydrateGtagConsent } from '@/lib/cookieConsent';

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    // Re-apply any previously stored decision to gtag's Consent Mode -
    // it defaults to "denied" on every fresh page load, so a returning
    // visitor who already said yes needs that re-sent, not re-asked.
    rehydrateGtagConsent();

    if (!hasDecided()) {
      // Small delay so it doesn't fight with the hero's own entrance
      // animation on first load.
      const t = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(t);
    }
  }, []);

  const accept = () => {
    setConsent({ calendly: true, analytics: true });
    setVisible(false);
  };

  const necessaryOnly = () => {
    setConsent({ calendly: false, analytics: false });
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:max-w-md z-[60]"
          data-testid="cookie-consent-banner"
          role="dialog"
          aria-live="polite"
          aria-label="Cookie consent"
        >
          <div className="bg-white rounded-2xl shadow-[0_8px_40px_rgba(0,51,51,0.16)] border border-slate-200 p-6">
            <div className="flex items-start gap-3 mb-3">
              <div className="shrink-0 w-9 h-9 rounded-full bg-[#F8FAFC] flex items-center justify-center">
                <Cookie className="w-4.5 h-4.5 text-[#003333]" />
              </div>
              <div>
                <p className="font-semibold text-[#003333] text-base leading-snug">
                  We use a couple of cookies
                </p>
                <p className="text-sm text-slate-500 leading-relaxed mt-1">
                  Essential ones keep the site running. With your consent we
                  also use Google Analytics and load our booking widget
                  (Calendly), both of which set their own cookies.
                </p>
              </div>
            </div>

            <AnimatePresence>
              {detailsOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="text-xs text-slate-500 leading-relaxed space-y-2 pb-3 pl-12">
                    <p>
                      <span className="font-semibold text-[#003333]">Essential</span> — required for the site to function (e.g. remembering your live chat conversation). Always on.
                    </p>
                    <p>
                      <span className="font-semibold text-[#003333]">Analytics (Google Analytics)</span> — helps us see which pages are useful and fix ones that aren't. No storage happens until you accept.
                    </p>
                    <p>
                      <span className="font-semibold text-[#003333]">Booking widget (Calendly)</span> — only loads on the consultation page, and only if you accept. Lets you book a call without leaving the site.
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <button
              onClick={() => setDetailsOpen((v) => !v)}
              className="text-xs font-medium text-slate-400 hover:text-[#003333] transition-colors inline-flex items-center gap-1 mb-4 pl-12"
              data-testid="cookie-consent-details-toggle"
            >
              {detailsOpen ? 'Hide details' : 'What does this mean?'}
              <ChevronDown
                className={`w-3 h-3 transition-transform ${detailsOpen ? 'rotate-180' : ''}`}
              />
            </button>

            <div className="flex gap-2 pl-12">
              <button
                onClick={accept}
                className="flex-1 bg-[#003333] text-white hover:bg-[#004d4d] rounded-full px-5 py-2.5 text-sm font-medium transition-all duration-300"
                data-testid="cookie-consent-accept"
              >
                Accept all
              </button>
              <button
                onClick={necessaryOnly}
                className="flex-1 bg-transparent border border-[#003333]/20 text-[#003333] hover:border-[#003333] rounded-full px-5 py-2.5 text-sm font-medium transition-all duration-300"
                data-testid="cookie-consent-necessary"
              >
                Necessary only
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
